import { isNil, pick } from 'ramda';
import { useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Alert as MuiAlert, Button, FormHelperText, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import {
  attributesToSubmit,
  initialValues,
  OrgTimeSheetAdjustmentFormData,
  validationSchema,
} from 'src/forms/orgTimeSheetAdjustmentForm';
import NestedTimeSheetPresenter from 'src/presenters/NestedTimeSheetPresenter';
import { IndexParams } from 'src/repositories/organizationAdmin/TimeSheetsRepository';
import { IndexParams as AdjustmentsIndexParams } from 'src/repositories/organizationAdmin/TimeSheetsAdjustmentsRepository';
import { OrgAdminEngagementTimeSheetDates } from 'src/types/resources/OrgAdminEngagementTimeSheetDates';
import { NestedTimeSheet } from 'src/types/resources/TimeSheet';
import clsx from 'src/utils/clsx';
import { getTimeSheetLongDate } from 'src/utils/date';
import { parseRoundedDecimalHours } from 'src/utils/decimalHours';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import useModals from 'src/hooks/useModals';
import useOrgTimeSheets from 'src/hooks/useOrgTimeSheets';
import useOrgTimeSheetAdjustments from 'src/hooks/useOrgTimeSheetAdjustments';
import useSnackbar from 'src/hooks/useSnackbar';
import Box from 'src/components/Box';
import TimeTrackingTotalTime from 'src/components/TimeTrackingTotalTime';
import DecimalHoursField from 'src/components/TimeTracking/components/DecimalHoursField';
import AlertPayPeriodAmountChanged from 'src/components/CreateTimeSheetAdjustmentForm/components/AlertPayPeriodAmountChanged';

import styles from './styles';

export type OrgTimeSheetAdjustmentFormProps = {
  engagementTimeSheet: OrgAdminEngagementTimeSheetDates;
  parentTimeSheet: NestedTimeSheet;
  searchParams: IndexParams;
  adjustmentsSearchParams: AdjustmentsIndexParams;
};

const OrgTimeSheetAdjustmentForm: React.FC<OrgTimeSheetAdjustmentFormProps> = props => {
  const { engagementTimeSheet, parentTimeSheet, searchParams, adjustmentsSearchParams } = props;

  const [formError, setFormError] = useState<string | null>(null);

  const { hideModal } = useModals();
  const { enqueueErrorSnackbar } = useSnackbar();
  const { loadEngagementTimeSheetDates, loadTotalEngagementTimeSheet } = useOrgTimeSheets();
  const { createTimeSheetsAdjustment, loadTimeSheetsAdjustments } = useOrgTimeSheetAdjustments();

  const originalHours = Number(parentTimeSheet.hours);

  const handleSubmit = async (
    formData: OrgTimeSheetAdjustmentFormData,
    { setErrors }: FormikHelpers<OrgTimeSheetAdjustmentFormData>,
  ) => {
    setFormError(null);
    try {
      await createTimeSheetsAdjustment(attributesToSubmit(parentTimeSheet.id, formData)).unwrap();
      loadEngagementTimeSheetDates(searchParams);
      loadTotalEngagementTimeSheet(searchParams);
      loadTimeSheetsAdjustments(adjustmentsSearchParams);
      hideModal();
    } catch (error: unknown) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const responseErrors = parseToFormikErrors(extractResponseErrors(error)) as Record<string, string>;
        setErrors(pick(['hours', 'clientComment'], responseErrors));
        setFormError(
          responseErrors.parentId
            ? 'This time entry can no longer be adjusted. Reload the page to see its latest state.'
            : responseErrors.nonFieldErrors ?? null,
        );
        return;
      }
      enqueueErrorSnackbar('Failed to save the adjustment');
    }
  };

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit: handleFormikSubmit,
    setFieldValue,
  } = useFormik<OrgTimeSheetAdjustmentFormData>({
    initialValues: initialValues(parentTimeSheet),
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnMount: true,
  });

  const newHours = parseRoundedDecimalHours(values.hours);
  const isHoursChanged = newHours !== originalHours;

  const handleHoursFieldChange = (value: string) => {
    setFieldValue('hours', value);
  };

  const renderDetail = (label: string, value: string) => (
    <Stack sx={styles.formFieldContainer}>
      <Typography sx={styles.label} variant="button" component="span">
        {label}:
      </Typography>
      <Typography sx={styles.value}>{value}</Typography>
    </Stack>
  );

  return (
    <>
      {formError && <MuiAlert severity="error">{formError}</MuiAlert>}
      <form noValidate onSubmit={handleFormikSubmit}>
        <Box sx={styles.wrapper}>
          <Typography sx={styles.title} variant="h3">
            Adjustment: {getTimeSheetLongDate(parentTimeSheet.timekeepingDate)}
          </Typography>
          <Stack sx={styles.details}>
            {renderDetail('Talent', engagementTimeSheet.talent.fullName)}
            {renderDetail('Engagement', engagementTimeSheet.name)}
            {renderDetail('PO', NestedTimeSheetPresenter.poLabel(parentTimeSheet.purchaseOrder))}
          </Stack>
          <Stack sx={styles.hoursRow}>
            <Stack sx={clsx(styles.formFieldContainer, [[styles.originalHoursContainer, true]])}>
              <Typography sx={styles.label} variant="button" component="span">
                Original Worked Hours:
              </Typography>
              <TimeTrackingTotalTime
                sx={styles.value}
                time={parentTimeSheet.hours}
                isTotalVisible={false}
                isSmall
                testId="time-tracking-org-adjust-original-hours-value"
              />
            </Stack>
            <Box sx={styles.newHoursColumn}>
              <Stack sx={styles.formFieldContainer}>
                <Typography sx={styles.label} variant="button" component="span">
                  New Worked Hours:
                </Typography>
                <DecimalHoursField
                  value={values.hours}
                  isError={Boolean(errors.hours)}
                  onChange={handleHoursFieldChange}
                  testId="time-tracking-org-adjust-hours-input"
                />
              </Stack>
              {errors.hours && (
                <FormHelperText error data-testid="time-tracking-org-adjust-hours-error">
                  {errors.hours}
                </FormHelperText>
              )}
            </Box>
          </Stack>
          {!isNil(newHours) && !errors.hours && (
            <Box sx={styles.row}>
              {/* the Org is not shown the Talent Pay Rate, so its message counts at the Talent Hourly Rate */}
              <AlertPayPeriodAmountChanged
                originalHours={originalHours}
                newHours={newHours}
                amount={Math.abs(newHours - originalHours) * parseFloat(engagementTimeSheet.hourlyFee)}
                viewer="organization"
              />
            </Box>
          )}
          <Box sx={styles.row}>
            <FormLabel sx={styles.descriptionLabel} htmlFor="clientComment">
              Description:
            </FormLabel>
            <TextField
              id="clientComment"
              name="clientComment"
              multiline
              rows={2}
              sx={styles.textarea}
              value={values.clientComment}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.clientComment && Boolean(errors.clientComment)}
              helperText={touched.clientComment && errors.clientComment}
            />
          </Box>
          <Box sx={styles.footer}>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={styles.button}
              loading={isSubmitting}
              disabled={!isHoursChanged || !isValid}
            >
              Save
            </LoadingButton>
            <Button variant="outlined" onClick={hideModal} sx={styles.button}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default OrgTimeSheetAdjustmentForm;
